import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { API, graphqlOperation } from 'aws-amplify';
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { showErrorSnackbar, showSuccessSnackbar } from '../snackBar/snakbar';
import { fetchCompanyList } from '../../redux/reducers/CompanySlice';
import { useThemes } from '../Theme/theme';
import { useLambdaFunctionUrl } from '../../hooks/useEmailSettingLambda';
import { Close, Security, Schedule } from '@mui/icons-material';
import { deleteFinancialYear } from '../../graphql';
import { setRemoveFY } from '../../redux/reducers/UserPreference';
import API_ENDPOINTS from '../../apiEndpoints';
import { useCreateCompanyPreferenceMutation, useListCompanyPreferenceQuery } from '../../redux/apis/companyPreference.api';

function ConfirmDeleteCompany({ type, openConfirm, setOpenConfirm, companyDetails, title, contentText, deleteFYId }) {
  const { theme } = useThemes();

  const [openOtp, setOpenOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [showResendOtp, setShowResendOtp] = useState(false);

  const dispatch = useDispatch();
  const { email, name, lastName, phone_number } = useSelector((state) => state?.userDetailsReducer?.userDetails);
  const { callLambdaFunctionUrl, error } = useLambdaFunctionUrl();

  // Format timer for display
  const formatTimer = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  const generateAndVerifyOTP = async (operation) => {
    try {
      setLoading(true);
      const purpose = type === 'company' ? 'Company Deletion' : 'FY Deletion';
      const payloadData = {
        type: operation,
        purpose: purpose,
        companyid: companyDetails?.CompanyID,
      };
      
      if (operation === 'verify') {
        // Validate and parse OTP properly - Enhanced debugging
        console.log('Full OTP Debug Info:', {
          otpState: otp,
          otpType: typeof otp,
          otpLength: otp?.length,
          timestamp: new Date().toISOString()
        });
        
        const otpValue = String(otp).trim();
        const hasOnlyDigits = /^\d+$/.test(otpValue);
        
        console.log('Validation Details:', { 
          originalOtp: otp, 
          trimmedOtp: otpValue, 
          length: otpValue.length,
          hasOnlyDigits: hasOnlyDigits,
          isValidLength: otpValue.length === 6,
          isValidFormat: /^\d{6}$/.test(otpValue),
          parsedValue: parseInt(otpValue, 10)
        });
        
        // More specific error messages
        if (!otpValue) {
          console.error('OTP Validation Failed: Empty OTP');
          showErrorSnackbar('OTP is required.');
          setLoading(false);
          return;
        }
        
        if (otpValue.length !== 6) {
          console.error('OTP Validation Failed: Wrong length', { expected: 6, actual: otpValue.length });
          showErrorSnackbar(`OTP must be exactly 6 digits. You entered ${otpValue.length} digits.`);
          setLoading(false);
          return;
        }
        
        if (!hasOnlyDigits) {
          console.error('OTP Validation Failed: Non-numeric characters', { value: otpValue });
          showErrorSnackbar('OTP must contain only numbers.');
          setLoading(false);
          return;
        }
        
        const parsedOTP = parseInt(otpValue, 10);
        payloadData.userEnteredOTP = parsedOTP;
        console.log('Final OTP Payload:', payloadData);
      }

      // Only proceed if validation passed
      if (operation === 'verify' && payloadData.userEnteredOTP === undefined) {
        setLoading(false);
        return;
      }

      await callLambdaFunctionUrl({
        url: API_ENDPOINTS.REACT_APP_SEND_OTP_URL,
        payload: payloadData,
        method: 'POST',
        onSuccess: async (data) => {
          if (operation === 'generate' && data?.data?.active === 1) {
            setOpenOtp(true);
            showSuccessSnackbar('OTP has been sent to your email successfully.');
            setTimer(900);
            setShowResendOtp(false);
          } else if (operation === 'verify' && data?.data?.valid) {
            setOtp('');
            await handleSuccessfulVerification();
          } else {
            showErrorSnackbar(
              operation === 'generate' 
                ? 'Failed to generate OTP. Please try again later.' 
                : 'Invalid OTP. Please try again.'
            );
          }
        },
        onError: (err) => {
          const errorMessage = err?.response?.data?.message || 'An unexpected error occurred. Please try again.';
          showErrorSnackbar(errorMessage);
          console.error('Error during OTP operation:', err);
        },
      });
    } catch (error) {
      console.error('Unexpected error:', error);
      const errorMessage = error?.response?.data?.message || 'An unexpected error occurred. Please try again later.';
      showErrorSnackbar(errorMessage);
    } finally {
      setLoading(false);
      if (operation === 'verify') {
        setOtp('');
      }
    }
  };

  const handleSuccessfulVerification = async () => {
    showSuccessSnackbar(`OTP verified successfully. Proceeding with ${type} deletion.`);
    handleCloseOtp();
    
    if (type === 'financialYear') {
      await deleteFY();
    } else {
      dispatch(fetchCompanyList());
    }
  };

  const [createCompanyPreference, { isLoading: creating }] = useCreateCompanyPreferenceMutation();
  const { data: companyPreference, isLoading, isFetching } = useListCompanyPreferenceQuery();

  const deleteFY = async () => {
    try {
      setLoading(true);
      await API.graphql(graphqlOperation(deleteFinancialYear, { financialyearid: deleteFYId }));
      
      if (companyPreference?.length) {
        const pendingFinancialYear = companyPreference?.find(
          (preference) => 
            preference.attributetypecd.startsWith('NewFY Creation') && 
            preference.attributevalue === deleteFYId
        );
        
        if (pendingFinancialYear) {
          const userPreferenceConfig = {
            additionalvalue: '',
            attributetypecd: 'NewFY Creation',
            attributevalue: 0,
          };
          await createCompanyPreference({
            createCompanyPreferenceInput: userPreferenceConfig,
          });
        }
      }
      
      dispatch(setRemoveFY(deleteFYId));
      dispatch(fetchCompanyList());
      
      setTimeout(() => {
        showSuccessSnackbar('Financial Year deleted successfully');
      }, 1000);
    } catch (error) {
      console.error('Error deleting financial year:', error);
      showErrorSnackbar('Something went wrong. Please wait a moment and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseConfirm = useCallback(() => {
    setOpenConfirm(false);
  }, [setOpenConfirm]);

  const handleConfirmDelete = useCallback(() => {
    generateAndVerifyOTP('generate');
  }, []);

  const handleCloseOtp = useCallback(() => {
    setOpenOtp(false);
    handleCloseConfirm();
  }, [handleCloseConfirm]);

  const handleOtpChange = useCallback((event) => {
    console.log('OTP Input Change:', { 
      receivedValue: event, 
      valueType: typeof event,
      valueLength: event?.length,
      timestamp: new Date().toISOString()
    });
    setOtp(event);
  }, []);

  const handleVerifyOtp = useCallback(() => {
    console.log('Verify OTP Button Clicked:', {
      currentOtpState: otp,
      otpLength: otp?.length,
      otpType: typeof otp,
      buttonDisabled: otp.length < 6 || loading,
      loading: loading
    });
    generateAndVerifyOTP('verify');
  }, [otp, loading]);

  const handleResendOtp = useCallback(async () => {
    setShowResendOtp(false);
    setTimer(900);
    await generateAndVerifyOTP('generate');
  }, []);

  const deleteCompanyDetails = async () => {
    handleCloseOtp();
    dispatch(fetchCompanyList());
  };

  // Timer effect
  useEffect(() => {
    let timerInterval;
    
    if (openOtp && timer > 0) {
      timerInterval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            setShowResendOtp(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [openOtp, timer]);

  // Comprehensive responsive modal styles
  const modalStyles = {
    dialog: {
      borderRadius: { xs: 2, sm: 3 },
      boxShadow: { xs: theme.shadows[4], sm: theme.shadows[8] },
      margin: { xs: '16px', sm: '32px', md: '40px', lg: '56px' },
      maxHeight: '90vh',
      overflow: 'hidden',
      maxWidth: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 64px)', md: '600px', lg: '640px' },
      width: { xs: 'auto', sm: 'auto', md: '600px', lg: '640px' },
    },
    dialogTitle: {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      display: 'flex',
      padding: { 
        xs: '12px 16px', 
        sm: '14px 20px', 
        md: '16px 24px' 
      },
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: { xs: '56px', sm: '60px', md: '64px' },
      flexWrap: 'nowrap',
    },
    titleText: {
      fontSize: { xs: '14px', sm: '16px', md: '18px' },
      fontWeight: 600,
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      marginRight: 1,
    },
    dialogContent: {
      padding: { 
        xs: '16px', 
        sm: '18px', 
        md: '20px', 
        lg: '22px' 
      },
      textAlign: 'center',
      overflowY: 'auto',
      maxHeight: '50vh',
      maxWidth: '100%',
    },
    contentText: {
      fontSize: { xs: '13px', sm: '14px', md: '15px' },
      color: theme.palette.text.primary,
      lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
      mb: { xs: 1, sm: 1.5, md: 2 },
      wordBreak: 'keep-all',
      hyphens: 'avoid',
      overflowWrap: 'normal',
      whiteSpace: 'normal',
      width: '100%',
      minWidth: '0',
      padding: { xs: '0', sm: '0', md: '4px 0', lg: '6px 0' },
      textAlign: 'center',
      display: 'block',
    },
    dialogActions: {
      padding: { 
        xs: '12px 16px', 
        sm: '14px 18px', 
        md: '16px 20px', 
        lg: '18px 22px' 
      },
      justifyContent: 'center',
      gap: { xs: 1.5, sm: 2, md: 2.5, lg: 2.5 },
      flexWrap: 'wrap',
    },
    buttonContainer: {
      display: 'flex',
      gap: { xs: 1, sm: 1.5, md: 2 },
      justifyContent: 'center',
      width: '100%',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: 'stretch',
    },
    button: {
      flex: { xs: 'none', sm: 1 },
      minWidth: { xs: '100%', sm: '120px', md: '140px' },
      py: { xs: 1.2, sm: 1.5, md: 1.5 },
      px: { xs: 2, sm: 3, md: 3 },
      fontSize: { xs: '13px', sm: '14px', md: '16px' },
      fontWeight: { xs: 500, sm: 600 },
      borderRadius: { xs: 1, sm: 2 },
      textTransform: 'none',
      boxShadow: { xs: theme.shadows[1], sm: theme.shadows[2], md: theme.shadows[2] },
      '&:hover': {
        boxShadow: { xs: theme.shadows[3], sm: theme.shadows[4], md: theme.shadows[4] },
      },
    },
    otpContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: { xs: 2, sm: 2.5, md: 3 },
      padding: { xs: '16px', sm: '20px', md: '24px' },
    },
    timerContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: { xs: 1, sm: 1.5, md: 2 },
      width: '100%',
      padding: { xs: '8px 12px', sm: '12px 16px' },
      backgroundColor: theme.palette.grey[50],
      borderRadius: { xs: 1, sm: 2 },
      border: `1px solid ${theme.palette.grey[200]}`,
      flexWrap: 'wrap',
      minHeight: { xs: '40px', sm: '44px' },
    },
    alertStyles: {
      mb: { xs: 2, sm: 2.5, md: 3 },
      textAlign: 'left',
      fontSize: { xs: '13px', sm: '14px', md: '15px' },
      padding: { xs: '10px 14px', sm: '12px 16px', md: '14px 18px' },
      '& .MuiAlert-message': {
        fontSize: { xs: '13px', sm: '14px', md: '15px' },
      },
    },
    warningText: {
      color: theme.palette.error.main,
      fontSize: { xs: '12px', sm: '13px', md: '14px' },
      fontWeight: { xs: 500, sm: 600 },
      textAlign: 'center',
      marginTop: { xs: 0.5, sm: 1, md: 1.5 },
      padding: { xs: '4px 0', sm: '6px 0', md: '8px 0' },
    },
    otpInput: {
      width: '100%',
      maxWidth: { xs: '280px', sm: '320px', md: '400px' },
      '& .MuiOtpInput-TextField': {
        '& .MuiOutlinedInput-root': {
          borderRadius: { xs: 1, sm: 2 },
          fontSize: { xs: '16px', sm: '18px', m: '20px' },
          fontWeight: 600,
          backgroundColor: theme.palette.grey[50],
          height: { xs: '48px', sm: '52px', md: '56px' },
        },
      },
    },
  };

  return (
    <>
      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth={{ xs: false, sm: 'sm', md: 'sm', lg: 'sm' }}
        fullWidth
        PaperProps={{
          sx: modalStyles.dialog,
        }}
      >
        <DialogTitle sx={modalStyles.dialogTitle}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, minWidth: 0, flex: 1 }}>
            <Security sx={{ fontSize: { xs: 18, sm: 20, md: 24 }, flexShrink: 0 }} />
            <Typography variant="h6" sx={modalStyles.titleText}>
              {title}
            </Typography>
          </Box>
          <IconButton 
            aria-label="close" 
            onClick={handleCloseConfirm}
            sx={{ 
              color: '#fff',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={modalStyles.dialogContent}>
          <Alert 
            severity="warning" 
            sx={modalStyles.alertStyles}
            icon={<Security />}
          >
            Important Security Notice
          </Alert>
          
          <DialogContentText 
            variant="body1" 
            sx={modalStyles.contentText}
          >
            {contentText}
          </DialogContentText>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontSize: '13px',
              fontStyle: 'italic',
              mb: 1,
            }}
          >
            An OTP will be sent to your registered email for verification.
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={modalStyles.warningText}
          >
            ⚠️ This action cannot be undone
          </Typography>
        </DialogContent>

        <DialogActions sx={modalStyles.dialogActions}>
          <Box sx={modalStyles.buttonContainer}>
            <Button
              onClick={handleCloseConfirm}
              variant="outlined"
              sx={modalStyles.button}
              disabled={loading}
            >
              Cancel
            </Button>

            <Button
              onClick={loading ? null : handleConfirmDelete}
              variant="contained"
              color="error"
              sx={modalStyles.button}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Confirm Delete'
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* OTP Dialog */}
      <Dialog 
        open={openOtp} 
        onClose={handleCloseOtp} 
        aria-labelledby="otp-dialog-title" 
        aria-describedby="otp-dialog-description"
        maxWidth={{ xs: false, sm: 'sm', md: 'sm', lg: 'sm' }}
        fullWidth
        PaperProps={{
          sx: modalStyles.dialog,
        }}
      >
        <DialogTitle sx={modalStyles.dialogTitle}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, minWidth: 0, flex: 1 }}>
            <Security sx={{ fontSize: { xs: 18, sm: 20, md: 24 }, flexShrink: 0 }} />
            <Typography variant="h6" sx={modalStyles.titleText}>
              Enter Verification Code
            </Typography>
          </Box>
          <IconButton 
            aria-label="close" 
            onClick={handleCloseOtp}
            sx={{ 
              color: '#fff',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={modalStyles.dialogContent}>
          <Box sx={modalStyles.otpContainer}>
            <Alert 
              severity="info" 
              sx={modalStyles.alertStyles}
              icon={<Security />}
            >
              Verification Required
            </Alert>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: { xs: '14px', sm: '16px' },
                  color: theme.palette.text.primary,
                  mb: 1,
                }}
              >
                We've sent a 6-digit verification code to:
              </Typography>
              
              <Chip
                label={email}
                variant="outlined"
                color="primary"
                sx={{ 
                  fontSize: { xs: '13px', sm: '14px' },
                  px: 1,
                  py: 0.5,
                }}
              />
            </Box>

            <MuiOtpInput 
              sx={modalStyles.otpInput}
              autoFocus 
              onChange={handleOtpChange} 
              value={otp} 
              length={6}
              gap={{ xs: 1, sm: 1.5, md: 2 }}
            />

            <Box sx={modalStyles.timerContainer}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: { xs: '12px', sm: '14px' },
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  Time remaining: {formatTimer(timer)}
                </Typography>
              </Box>
              
              <Button 
                variant="text" 
                onClick={handleResendOtp} 
                color="primary" 
                size="small"
                disabled={loading}
                sx={{
                  fontSize: { xs: '12px', sm: '14px' },
                  textTransform: 'none',
                  minWidth: 'auto',
                  fontWeight: 500,
                }}
              >
                Resend OTP
              </Button>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={modalStyles.dialogActions}>
          <Box sx={modalStyles.buttonContainer}>
            <Button 
              variant="outlined" 
              onClick={handleCloseOtp} 
              color="primary"
              sx={modalStyles.button}
              disabled={loading}
            >
              Cancel
            </Button>
            
            <Button
              variant="contained"
              onClick={loading ? null : handleVerifyOtp}
              color="primary"
              disabled={otp.length < 6 || loading}
              sx={modalStyles.button}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Verify & Proceed'
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}

// PropTypes validation
ConfirmDeleteCompany.propTypes = {
  type: PropTypes.oneOf(['company', 'financialYear']).isRequired,
  openConfirm: PropTypes.bool.isRequired,
  setOpenConfirm: PropTypes.func.isRequired,
  companyDetails: PropTypes.object,
  title: PropTypes.string.isRequired,
  contentText: PropTypes.string.isRequired,
  deleteFYId: PropTypes.string,
};

export default ConfirmDeleteCompany;